/**
 * Currency formatting utilities for EGP (Egyptian Pound)
 */

/**
 * Format a number as EGP currency
 * @param value - The numeric value to format
 * @param options - Optional formatting options
 * @returns Formatted currency string (e.g., "EGP 1,000.00")
 */
export const formatCurrency = (
  value: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  return new Intl.NumberFormat('en-EG', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(value);
};

/**
 * Format EGP in Arabic (ج.م - جنيه مصري) for cart/offers
 */
export const formatCurrencyArabic = (
  value: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  return new Intl.NumberFormat('ar-EG', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  }).format(value);
};

/** Replace " LE" in discount descriptions with Arabic " ج.م" */
export const descriptionWithArabicCurrency = (description: string): string => {
  return description.replace(/\s*LE\s*/gi, ' ج.م ');
};

/**
 * Format a number as EGP currency in compact notation
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "EGP 1K" or "EGP 1M")
 */
export const formatCurrencyCompact = (value: number): string => {
  return new Intl.NumberFormat('en-EG', {
    style: 'currency',
    currency: 'EGP',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

/**
 * Format a number without currency symbol
 * @param value - The numeric value to format
 * @param options - Optional formatting options
 * @returns Formatted number string (e.g., "1,000.00")
 */
export const formatNumber = (
  value: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  return new Intl.NumberFormat('en-EG', {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(value);
};

